// Справочник товаров на устройстве: поиск по названию, артикулу и штрихкоду без интернета.
// Обновляется при входе, раз в 15 минут и когда связь возвращается.
import { api } from '../../api/client.ts';
import { kvGet, kvSet } from './store.ts';
import { matchesProductSearch } from './productSearch.ts';

export interface CatProduct {
  id: number; sku: string; name: string; unit: string; is_kit: boolean; kit_status: string | null; article_1c: string | null;
  image_url: string | null; purchase_price?: number | null; barcodes: string[]; components: [number, number][] | null; physical: number;
}
interface Catalog { taken_at: string; warehouse_id: number | null; products: CatProduct[] }

let cat: Catalog | null = null;
let byId = new Map<number, CatProduct>();
let byCode = new Map<string, CatProduct>();
let loading: Promise<void> | null = null;


function index(c: Catalog) {
  cat = c;
  byId = new Map(c.products.map((p) => [p.id, p]));
  byCode = new Map();
  for (const p of c.products) for (const b of p.barcodes ?? []) byCode.set(b, p);
}
async function ensure() {
  if (cat) return;
  loading ??= (async () => { const c = await kvGet<Catalog>('catalog'); if (c && !cat) index(c); })();
  await loading;
}
/** Обновить справочник с сервера; без связи остаётся прежний */
export async function refreshCatalog(warehouseId: number | null): Promise<boolean> {
  try {
    const c = await api<Catalog>(`/products/offline-catalog${warehouseId ? `?warehouse_id=${warehouseId}` : ''}`);
    index(c);
    await kvSet('catalog', c);
    return true;
  } catch { await ensure(); return false; }
}
export async function catalogInfo() { await ensure(); return cat ? { taken_at: cat.taken_at, count: cat.products.length } : null; }

const asRow = (p: CatProduct) => ({ ...p, _offline: true });
export async function searchLocal(q: string, limit = 20, baseOnly = false) {
  await ensure();
  if (!cat) return [];
  // Все совпадения, включая части дополнительных штрихкодов. Наборы исключаем до лимита.
  const exact = byCode.get(q.trim());
  const out: CatProduct[] = exact && (!baseOnly || !exact.is_kit) ? [exact] : [];
  for (const p of cat.products) {
    if (out.length >= limit) break;
    if (p.id === exact?.id) continue;
    if ((!baseOnly || !p.is_kit) && matchesProductSearch(p, q)) { out.push(p); if (out.length >= limit) break; }
  }
  return out.map(asRow);
}
export async function findByCodeLocal(code: string) {
  await ensure();
  if (!cat) return null;
  const p = byCode.get(code) ?? cat.products.find((x) => x.sku === code) ?? null;
  return p ? asRow(p) : null;
}
/** Состав набора из справочника — в том же виде, что отдаёт сервер */
export async function componentsLocal(kitId: number) {
  await ensure();
  const kit = byId.get(kitId);
  return (kit?.components ?? []).map(([id, qty]) => {
    const c = byId.get(id);
    return c ? { product_id: c.id, sku: c.sku, name: c.name, unit: c.unit, is_kit: c.is_kit, purchase_price: c.purchase_price ?? null,
                 qty_per_kit: qty, barcode: c.barcodes?.[0] ?? null, physical: c.physical } : null;
  }).filter(Boolean) as any[];
}
export async function searchLocalById(id: number) {
  await ensure();
  const p = byId.get(id);
  return p ? asRow(p) : null;
}
